import { useQueries } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"
import { trpc } from "lib/trpc"
import { ChevronDown, ClipboardList, Eye, FileText, FolderOpen, Shield, Target } from "lucide-react"
import { useMemo } from "react"
import { Page } from "@/components/page"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Typography } from "@/components/ui/typography"

export const Route = createFileRoute("/_authed/_professional/professional/clients")({
  component: RouteComponent,
})

interface ClientEstate {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
}

function RouteComponent() {
  const { data: estates = [], isLoading: isLoadingEstates, error } = trpc.estates.list.useQuery()
  const trpcContext = trpc.useContext()

  // Fetch owner info for each estate using useQueries
  const ownerQueries = useQueries({
    queries: estates.map((estate) => ({
      queryKey: ["estates", "getEstateOwners", { estateId: estate.id }],
      queryFn: () => trpcContext.estates.getEstateOwners.fetch({ estateId: estate.id }),
      enabled: estates.length > 0,
    })),
  })

  const isLoadingOwners = ownerQueries.some((q) => q.isLoading)
  const isLoading = isLoadingEstates || isLoadingOwners

  // Merge estate data with owner info
  const clientEstates: ClientEstate[] = useMemo(() => {
    return estates.map((estate, index) => {
      const ownerData = ownerQueries[index]?.data
      const owner = ownerData?.[0] // Get the first owner (ADMIN)
      return {
        id: estate.id,
        firstName: owner?.firstName ?? null,
        lastName: owner?.lastName ?? null,
        email: owner?.email ?? null,
      }
    })
  }, [estates, ownerQueries])

  const columns: ColumnDef<ClientEstate>[] = [
    {
      accessorKey: "firstName",
      header: "First Name",
      cell: ({ row }) => row.getValue("firstName") || "-",
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      cell: ({ row }) => row.getValue("lastName") || "-",
    },
    {
      accessorKey: "email",
      header: "Email Address",
      cell: ({ row }) => row.getValue("email") || "-",
    },
    {
      id: "clientData",
      header: "Client Data",
      cell: ({ row }) => {
        const estate = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <FolderOpen className="h-4 w-4 mr-2" />
                Client Data
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Navigate to</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/professional/goals/$estateId" params={{ estateId: estate.id }}>
                  <Target className="h-4 w-4 mr-2" />
                  Goals
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/professional/guided-forms/$estateId"
                  params={{ estateId: estate.id }}
                  search={{ currentStep: "intro" }}
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Guided Form
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
    {
      id: "reports",
      header: "Reports",
      cell: ({ row }) => {
        const estate = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-2" />
                Reports
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>View report</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/professional/reports/will/$estateId" params={{ estateId: estate.id }}>
                  <FileText className="h-4 w-4 mr-2" />
                  Will
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/professional/reports/insurance/$estateId"
                  params={{ estateId: estate.id }}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Insurance
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/professional/reports/$estateId" params={{ estateId: estate.id }}>
                  <Eye className="h-4 w-4 mr-2" />
                  Comprehensive
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Clients
        </Typography>
      }
    >
      <div className="space-y-8">
        <div className="rounded-lg border p-6">
          <Typography as="h2" size="lg" className="mb-4">
            Client Estates
          </Typography>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="font-medium text-red-800">Error</p>
              <p className="text-red-700">{error.message}</p>
            </div>
          )}

          {isLoading ? (
            <div>Loading clients...</div>
          ) : (
            <DataTable columns={columns} data={clientEstates} />
          )}
        </div>
      </div>
    </Page>
  )
}
